import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import { featuredProducts as dataFeaturedProducts } from '../data/products';

const Home = () => {
  const featuredProducts = [
    {
      title: "Nintendo Joycon Pikachu",
      subtitle: "Control. Connect. Conquer",
      image: "/images/home/pikachu.png",
      bgColor: "from-blue-900/50 to-black",
      path: "/products/nintendo-joycon-pikachu"
    },
    {
      title: "Wireless Keyboard",
      subtitle: "3-Mode Wireless Bluetooth Keyboard and Mouse Set",
      image: "/images/home/wireless.png",
      bgColor: "from-green-900/50 to-black",
              path: "/products/wireless-keyboard"
    },
    {
      title: "Karaoke Speaker",
      subtitle: "DUAL WIRELESS MICROPHONES & RGB LIGHTING",
      image: "/images/home/karaoke.png",
      bgColor: "from-purple-900/50 to-black",
      path: "/products/gaming-headset"
    }
  ];

  const serviceFeatures = [
    {
      title: "QUALITY ASSURANCE",
      subtitle: "3-Step Quality Check",
      description: "Every product undergoes 3 rigorous quality inspections to ensure perfect condition",
      icon: "🔍",
      bgColor: "from-red-900/50 to-black",
      path: "/about"
    },
    {
      title: "FAST SHIPPING",
      subtitle: "Express Delivery",
      description: "24-hour processing with 3-5 business days delivery worldwide",
      icon: "🚚",
      bgColor: "from-blue-900/50 to-black",
      path: "/support"
    },
    {
      title: "CUSTOMER SUPPORT",
      subtitle: "24/7 Support",
      description: "Professional customer service team available around the clock",
      icon: "🎧",
      bgColor: "from-green-900/50 to-black",
      path: "/support"
    },
    {
      title: "100% BRAND NEW",
      subtitle: "All Items Guaranteed",
      description: "Every product is 100% brand new, never used or refurbished",
      icon: "✨",
      bgColor: "from-yellow-900/50 to-black",
      path: "/support"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Top Announcement Bar */}
      <div className="bg-gradient-to-r from-blue-800 via-purple-800 to-pink-800 text-white text-center py-3 px-4">
        <p className="text-sm">
          Gaming Season Sale: Power up with CHILL GAMING gear and unlock exclusive in-game rewards! 
          <Link to="/shop" className="ml-2 text-white underline hover:text-gray-300">
            Learn More &gt;
          </Link>
        </p>
      </div>
      
      {/* Featured Products */}
      <div className="space-y-1">
        {featuredProducts.map((product, index) => (
          <motion.div
            key={index}
            className="relative h-[400px] sm:h-[500px] md:h-[600px] lg:h-[800px] xl:h-[1000px] overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
          >
            {/* Background */}
            <div className={`absolute inset-0 bg-gradient-to-b ${product.bgColor}`}>
              <img
                src={product.image}
                alt={product.title}
                className="w-full h-full object-cover object-center opacity-50"
              />
            </div>

            {/* Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 sm:p-6 md:p-8">
              <motion.h2
                className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-bold mb-4 sm:mb-6 md:mb-8 text-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 + 0.2 }}
              >
                {product.title}
              </motion.h2>
              
              <motion.p
                className="text-lg sm:text-xl md:text-3xl lg:text-4xl text-white mb-6 sm:mb-8 md:mb-12 max-w-4xl px-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 + 0.3 }}
              >
                {product.subtitle}
              </motion.p>

              <div className="flex justify-center">
                <Link to={product.path}>
                  <motion.button
                    className="px-6 py-3 sm:px-8 sm:py-4 md:px-12 md:py-6 bg-white text-black font-bold rounded hover:bg-opacity-90 transition-all text-sm sm:text-base md:text-xl"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Buy Now &gt;
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Grid Products Display */}
      <div className="p-1">
        <motion.h2 
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center my-8 sm:my-10 md:my-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          FEATURED PRODUCTS
        </motion.h2>

        {/* Product Cards Display */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 px-2 sm:px-4">
          {dataFeaturedProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
        
        {/* Service Features Process Flow */}
        <div className="mt-12 sm:mt-16 md:mt-20 px-2 sm:px-4 py-8 sm:py-12 md:py-16 bg-gradient-to-r from-blue-900/20 via-purple-900/20 to-pink-900/20 rounded-2xl sm:rounded-3xl mx-2 sm:mx-4">
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-12 sm:mb-16 md:mb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Our Service Process
          </motion.h2>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 sm:gap-8 md:gap-8">
            {serviceFeatures.map((feature, index) => (
              <React.Fragment key={index}>
                {/* Service Step */}
                <motion.div
                  className="flex flex-col items-center text-center max-w-xs px-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.2 }}
                >
                  {/* Icon Circle */}
                  <motion.div
                    className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-xl"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <span className="text-2xl sm:text-3xl md:text-4xl">{feature.icon}</span>
                  </motion.div>
                  
                  {/* Title */}
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  
                  {/* Subtitle */}
                  <h4 className="text-sm sm:text-base md:text-lg font-semibold text-yellow-400 mb-2 sm:mb-3">
                    {feature.subtitle}
                  </h4>
                  
                  {/* Description */}
                  <p className="text-xs sm:text-sm md:text-base text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>

                {/* Arrow (except for last item) */}
                {index < serviceFeatures.length - 1 && (
                  <motion.div
                    className="hidden md:block text-white text-4xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 + index * 0.2 }}
                  >
                    →
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;